import type { PillTone } from './Pill';

/** Canonical five-stage lifecycle status — mirrors the domain TrialStatus. */
export type ProjectStatus = 'setting_up' | 'generating' | 'in_review' | 'finalised' | 'failed';

/**
 * Surface Status — see GLOSSARY.md. The client-approved status surface is
 * "In progress" / "Done" only (UAT-02); `failed` keeps a distinct error
 * surface so a dead generation never masquerades as progressing (ruled by
 * Tom, 2026-06-12).
 */
export type SurfaceStatus = 'in_progress' | 'done' | 'failed';

export type SurfaceStatusPill = {
  readonly tone: PillTone;
  readonly label: string;
};

export const surfaceStatusFor = (status: ProjectStatus): SurfaceStatus => {
  switch (status) {
    case 'setting_up':
    case 'generating':
    case 'in_review':
      return 'in_progress';
    case 'finalised':
      return 'done';
    case 'failed':
      return 'failed';
  }
};

const SURFACE_STATUS_PILLS: Record<SurfaceStatus, SurfaceStatusPill> = {
  in_progress: { tone: 'neutral', label: 'In progress' },
  done: { tone: 'success', label: 'Done' },
  failed: { tone: 'danger', label: 'Failed' },
};

/** The pill rendered for a stage on Dashboard cards and the in-trial TopBar. */
export const surfaceStatusPill = (status: ProjectStatus): SurfaceStatusPill =>
  SURFACE_STATUS_PILLS[surfaceStatusFor(status)];

const SURFACE_STATUS_CHIPS: Record<SurfaceStatus, string> = {
  in_progress: 'bg-stage-progress-bg text-stage-progress-fg',
  done: 'bg-stage-done-bg text-stage-done-fg',
  failed: 'bg-danger-bg text-danger-fg',
};

/**
 * Dashboard card corner-chip fills — the exact approved values (Figma
 * 1061:63455 / 1061:63567, tokens in the tailwind preset). `failed` has no
 * approved chip; it reuses the danger tone (UAT-02 ruling, 2026-06-12).
 */
export const surfaceStatusChipClasses = (status: ProjectStatus): string =>
  SURFACE_STATUS_CHIPS[surfaceStatusFor(status)];
